import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { LogOut, LayoutGrid, Coins, BarChart3 } from "lucide-react";

export function TopBar({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [unlimited, setUnlimited] = useState(false);
  const [pending, setPending] = useState(0);
  const prevPending = useRef(0);
  const initialized = useRef(false);

  useEffect(() => {
    const fetchBalance = () =>
      api.get("/billing/me").then((r) => {
        setBalance(r.data.balance);
        setUnlimited(!!r.data.unlimited);
      }).catch(() => {});
    fetchBalance();
    const t = setInterval(fetchBalance, 15000);
    return () => clearInterval(t);
  }, []);

  // Admin: poll pending UPI approvals and toast when new ones arrive
  useEffect(() => {
    if (user?.role !== "admin") return;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/billing/upi/admin/pending");
        if (!active) return;
        if (initialized.current && data.length > prevPending.current) {
          toast.info(`New UPI payment awaiting approval (${data.length} pending)`);
        }
        initialized.current = true;
        prevPending.current = data.length;
        setPending(data.length);
      } catch {
        /* ignore */
      }
    };
    poll();
    const t = setInterval(poll, 20000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [user?.role]);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const initials = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center gap-4 px-6 py-3">
        <Link to="/dashboard" data-testid="topbar-home-link">
          <Logo />
        </Link>
        <div className="flex-1">{children}</div>
        <ThemeToggle />
        <Link to="/billing" data-testid="topbar-billing-button" className="relative">
          <Button variant="ghost" size="sm" className="rounded-sm">
            <Coins size={16} className="mr-2 text-primary" />
            {balance === null ? "Billing" : unlimited ? "Unlimited" : `${balance} credits`}
          </Button>
          {user?.role === "admin" && pending > 0 && (
            <span
              className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground"
              data-testid="upi-pending-badge"
            >
              {pending}
            </span>
          )}
        </Link>
        <Link to="/dashboard">
          <Button variant="ghost" size="sm" className="rounded-sm" data-testid="topbar-projects-button">
            <LayoutGrid size={16} className="mr-2" /> Projects
          </Button>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex h-9 w-9 items-center justify-center rounded-sm border border-border bg-secondary text-sm font-medium"
              data-testid="user-menu-trigger"
            >
              {initials}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5 text-sm">
              <div className="font-medium">{user?.name}</div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
            </div>
            <DropdownMenuSeparator />
            {user?.role === "admin" && (
              <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                <BarChart3 size={15} className="mr-2" /> Admin console
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => navigate("/billing")} data-testid="menu-billing">
              <Coins size={15} className="mr-2" /> Billing &amp; credits
            </DropdownMenuItem>
            <DropdownMenuItem onClick={doLogout} data-testid="logout-button">
              <LogOut size={15} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
